import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { AlertBanner, Button, TruncatedTextWithTooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { SignalTypeChip } from "app/pages/sales-intelligence/modules/signals/components/SignalTypeChip/SignalTypeChip";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";

import signalRecommenderI18nKeys from "../../utils/i18n";

import { FilterPanelSlot } from "./FilterPanelSlot";
import { StyledConfirmModalContent, StyledFileHeader, StyledSplitFooter } from "./styles";
import type { FilterPanelDescriptor } from "./types";

type ConfirmStepProps = {
    readonly fileName: string;
    readonly companiesCount: number;
    /** Signal types this enrichment will look for, in display order. */
    readonly signalTypes: readonly SignalType[];
    readonly filters: readonly FilterPanelDescriptor[];
    readonly onCancel: VoidFunction;
    readonly onAccept: VoidFunction;
};

export const ConfirmStep = ({
    fileName,
    companiesCount,
    signalTypes,
    filters,
    onCancel,
    onAccept,
}: ConfirmStepProps) => {
    const { translate } = useTranslate();

    const companiesIdentified = signalRecommenderI18nKeys.confirm.companiesIdentified({
        count: formatNumberWithComa(companiesCount),
    });

    return (
        <>
            <StyledFileHeader withBorder>
                <SWReactIcons
                    iconName="csv-file"
                    size="sm"
                />
                <TruncatedTextWithTooltip
                    fontVariant="h5_2"
                    color={colorsPalettes.carbon[500]}
                    width="100%"
                    data-automation="signal-recommender-confirm-modal-title"
                >
                    {fileName}
                </TruncatedTextWithTooltip>
            </StyledFileHeader>

            <StyledConfirmModalContent>
                <Stack
                    gap="16px"
                    padding="16px 0px"
                >
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate(signalRecommenderI18nKeys.confirm.signals.title)}
                    </Text>

                    <Flex
                        data-automation="signal-recommender-confirm-signals"
                        flexWrap="wrap"
                        gap="8px"
                    >
                        {signalTypes.map((signalType) => (
                            <SignalTypeChip
                                key={signalType}
                                signalType={signalType}
                                label={translate(signalsI18nKeys.signalType(signalType))}
                                dataAutomation={`signal-recommender-confirm-signal-${signalType}`}
                            />
                        ))}
                    </Flex>

                    {/* Sits in the outer stack rather than the filters' own: it reads as the lead-in to the
                        category pickers, but needs the wider gap to stop crowding their heading. */}
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate(signalRecommenderI18nKeys.confirm.explanation)}
                    </Text>

                    <Stack gap="8px">
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate(signalRecommenderI18nKeys.confirm.filters.section.title)}
                        </Text>

                        <Stack
                            width="100%"
                            gap="8px"
                        >
                            {filters.map((filter) => (
                                <FilterPanelSlot
                                    key={filter.key}
                                    descriptor={filter}
                                />
                            ))}
                        </Stack>
                    </Stack>

                    <AlertBanner
                        variant="info"
                        data-automation="signal-recommender-confirm-email-notice"
                    >
                        <AlertBanner.Image />
                        <AlertBanner.Description>
                            {translate(signalRecommenderI18nKeys.confirm.emailNotice)}
                        </AlertBanner.Description>
                    </AlertBanner>
                </Stack>
            </StyledConfirmModalContent>

            <StyledSplitFooter>
                <Text
                    data-automation="signal-recommender-confirm-companies-count"
                    fontVariant="body2"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate(companiesIdentified.key, companiesIdentified.replacements)}
                </Text>

                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Button
                        type="flat"
                        onClick={onCancel}
                        dataAutomation="signal-recommender-confirm-cancel"
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        onClick={onAccept}
                        dataAutomation="signal-recommender-confirm-accept"
                    >
                        {translate(signalRecommenderI18nKeys.confirm.enrichDownload)}
                    </Button>
                </Flex>
            </StyledSplitFooter>
        </>
    );
};
